import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import { Download, EllipsisVertical, Mail } from 'lucide-react';
import invoiceDocumentIcon from '@/assets/images/invoice-document.svg';
import invoiceArchiveIcon from '@/assets/images/invoice-download.svg';
import invoiceDeleteIcon from '@/assets/images/trash-bin-trash.svg';
import {
  deleteClientInvoice,
  getClientInvoice,
  getClientInvoices,
  resendInvoiceEmail,
  unarchiveClientInvoice,
  updateInvoiceStatus,
  type Invoice,
} from '@/lib/api/invoices';
import { format } from 'date-fns';
import PaginationControls from '@/components/shared/PaginationControls';
import CreateInvoiceFlowDialog from '@/components/dashboard/CreateInvoiceFlowDialog';
import InvoiceDetailsDialog from '@/components/dashboard/InvoiceDetailsDialog';
import InvoiceConfirmDialog, { type InvoiceConfirmVariant, type InvoiceDialogFeedback } from '@/components/dashboard/InvoiceConfirmDialog';
import InvoiceStatusConfirmDialog from '@/components/dashboard/InvoiceStatusConfirmDialog';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { downloadInvoicePdf, getInvoicePdfFallbackFromProfile, invoiceApiToPdfPayload, withInvoiceClinicLogoDataUrl } from '@/utils/invoicePdfExport';
import {
  formatInvoiceCurrencyAmount,
  normalizeInvoiceCurrency,
} from '@/utils/invoiceCurrency';
import { Tag } from '@/components/ui/tag';

/** Fixed payload for marking paid from the list (no user input). */
const LIST_MARK_PAID_PAYMENT_METHOD = 'upi';

const getApiErrorMessage = (error: unknown): string => {
  const maybeObj = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    maybeObj?.response?.data?.error ||
    maybeObj?.response?.data?.message ||
    maybeObj?.message ||
    'Could not update invoice status'
  );
};

interface ClientDetailsV2InvoicesTabProps {
  clientId: string;
}

type StatusSelectValue = 'due' | 'overdue' | 'paid' | 'discarded';

function normalizeStatusForSelect(status?: string): StatusSelectValue {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'discarded' || s === 'archived') return 'discarded';
  if (s === 'sent') return 'due';
  if (s === 'overdue') return 'overdue';
  return 'due';
}

function statusLabelForSelect(value: StatusSelectValue): string {
  switch (value) {
    case 'due':
      return 'Due';
    case 'overdue':
      return 'Overdue';
    case 'paid':
      return 'Paid';
    case 'discarded':
      return 'Discarded';
    default:
      return value;
  }
}

function getStatusColor(value: StatusSelectValue): string {
  switch (value) {
    case 'paid':
      return '#28c76f';
    case 'overdue':
      return '#ea5455';
    case 'due':
      return '#ea5455';
    case 'discarded':
      return '#64748b';
    default:
      return '#64748b';
  }
}

function mapUiStatusToApiStatus(value: StatusSelectValue): string {
  if (value === 'due') return 'sent';
  return value;
}

function isInvoiceArchivedOrDiscarded(inv: Invoice | null | undefined): boolean {
  const s = (inv?.status || '').toLowerCase();
  return s === 'archived' || s === 'discarded';
}

function canSendInvoiceReminder(inv: Invoice | null | undefined): boolean {
  if (!inv?.id) return false;
  const status = normalizeStatusForSelect(inv.status);
  return status !== 'paid' && status !== 'discarded';
}

type InvoiceActionKey = 'view' | 'reminder' | 'archive' | 'delete' | 'download';

const INVOICE_MENU_DOWNLOAD_ICON_COLOR = '#57b7de';
const INVOICE_MENU_REMINDER_ICON_COLOR = '#20AEDC';

const CLIENT_INVOICE_PAGE_SIZE = 20;

const invoiceActionItems: Array<{
  key: InvoiceActionKey;
  label: string;
  iconSrc?: string;
  iconAlt: string;
  iconSize: number;
}> = [
  { key: 'view', label: 'View Invoice', iconSrc: invoiceDocumentIcon, iconAlt: 'View invoice', iconSize: 18 },
  { key: 'reminder', label: 'Send Reminder', iconAlt: 'Send invoice reminder', iconSize: 18 },
  { key: 'archive', label: 'Archive', iconSrc: invoiceArchiveIcon, iconAlt: 'Archive invoice', iconSize: 18 },
  { key: 'delete', label: 'Delete', iconSrc: invoiceDeleteIcon, iconAlt: 'Delete invoice', iconSize: 20 },
  { key: 'download', label: 'Download', iconAlt: 'Download invoice PDF', iconSize: 18 },
];

function formatInvoiceAmount(inv: Invoice): string {
  return formatCurrencyAmount(inv.totalDue ?? inv.amount ?? 0, inv.currency);
}

function formatCurrencyAmount(value: number | string | null | undefined, currency?: string | null): string {
  const normalizedCurrency = normalizeInvoiceCurrency(currency);
  const rawAmount = Number(value);
  const amount = Number.isFinite(rawAmount) ? rawAmount : 0;

  if (!normalizedCurrency) {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return formatInvoiceCurrencyAmount(amount, normalizedCurrency);
}

const invoiceTableColumns = '1fr 1.5fr 1fr 1fr 1fr 130px 100px';

const rowGrid = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: invoiceTableColumns,
  },
  gap: 1,
  alignItems: 'center',
  px: 2,
  py: 2,
} as const;

/** Create Invoice — brand fill aligned with invoice tabs accent (`#20AEDC`). */
const createInvoiceToolbarButtonSx = {
  minHeight: 42,
  px: 2,
  borderRadius: '10px',
  bgcolor: '#20AEDC',
  color: '#fff',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: 1.25,
  boxShadow: 'none',
  width: { xs: '100%', sm: 'auto' },
  '& .MuiButton-startIcon': { color: '#fff' },
  '&:hover': {
    bgcolor: '#1b9bc4',
    boxShadow: 'none',
  },
} as const;

/** Toolbar row under KPIs — matches `InvoicingPage` tabs + filters strip. */
const invoicingTabsToolbarRowSx = {
  borderBottom: '0.67px solid rgba(0,0,0,0.1)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
  '@media (min-width: 1240px)': {
    flexWrap: 'nowrap',
  },
} as const;

/** Match `InvoicingPage` tab count chips (All / Archived). */
const tabCountChipSx = {
  height: 20,
  minWidth: 28,
  fontSize: '12px',
  lineHeight: '16px',
  display: 'inline-flex',
  alignItems: 'center',
} as const;

const selectSx = {
  width: 120,
  height: 36,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.07)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.07)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.07)',
  },
  '&.Mui-focused': {
    boxShadow: 'none',
  },
  '& .MuiSelect-select': {
    py: '6px',
    pl: 2,
    pr: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiSelect-icon': {
    color: '#646464',
    right: 6,
  },
};

export const ClientDetailsV2InvoicesTab: React.FC<ClientDetailsV2InvoicesTabProps> = ({ clientId }) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [totalBilled, setTotalBilled] = useState<number>(0);
  const [totalPaid, setTotalPaid] = useState<number>(0);
  const [totalDue, setTotalDue] = useState<number>(0);
  /** Full-tab spinner: first load or `clientId` change only — not when switching All / Archived. */
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /** List/table refresh while KPI cards stay mounted (tab or refetch). */
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const expectFullPageSpinnerRef = useRef(true);
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [isInvoiceDetailsOpen, setIsInvoiceDetailsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [pdfDownloadingId, setPdfDownloadingId] = useState<string | null>(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(null);
  const [remindingInvoiceId, setRemindingInvoiceId] = useState<string | null>(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [activeActionRowIndex, setActiveActionRowIndex] = useState<number | null>(null);

  const [pendingStatusChange, setPendingStatusChange] = useState<{ invoice: Invoice; next: StatusSelectValue } | null>(null);
  const [pendingInvoiceConfirm, setPendingInvoiceConfirm] = useState<{
    variant: InvoiceConfirmVariant;
    invoice: Invoice;
  } | null>(null);
  const [invoiceStatusFeedback, setInvoiceStatusFeedback] = useState<InvoiceDialogFeedback | null>(null);
  const [invoiceConfirmFeedback, setInvoiceConfirmFeedback] = useState<InvoiceDialogFeedback | null>(null);

  /** 0 = All (no status filter), 1 = Archived (`GET .../client-invoices?clientId=&status=archived`). */
  const [invoiceListTab, setInvoiceListTab] = useState(0);
  const [invoiceListPage, setInvoiceListPage] = useState(1);
  const [invoiceListTotalPages, setInvoiceListTotalPages] = useState(1);
  /** Chip counts; survives empty archived fetch so the tab shell still shows when All has rows. */
  const [listTabCounts, setListTabCounts] = useState<{ all: number; archived: number }>({ all: 0, archived: 0 });
  const nutritionistCurrency = normalizeInvoiceCurrency(user?.currency);

  useEffect(() => {
    expectFullPageSpinnerRef.current = true;
  }, [clientId]);

  useEffect(() => {
    setInvoiceListPage(1);
  }, [clientId]);

  const loadInvoices = useCallback(async () => {
    const useFullPageSpinner = expectFullPageSpinnerRef.current;
    try {
      if (useFullPageSpinner) {
        setIsLoading(true);
      } else {
        setIsListLoading(true);
      }
      const data = await getClientInvoices({
        clientId,
        status: invoiceListTab === 1 ? 'archived' : undefined,
        page: invoiceListPage,
        limit: CLIENT_INVOICE_PAGE_SIZE,
        currency: nutritionistCurrency ?? undefined,
      });
      const fetchedInvoices: Invoice[] = Array.isArray(data?.invoices)
        ? data.invoices
        : (Array.isArray(data) ? data : []);
      setInvoices(fetchedInvoices);

      const pagination = data?.pagination as { totalPages?: number; total?: number } | undefined;
      const listTotal = Number(pagination?.total);
      const counts = data?.countsByStatus as { all?: number; archived?: number } | undefined;

      const totalForPaging =
        invoiceListTab === 1
          ? Number(counts?.archived) || (Number.isFinite(listTotal) ? listTotal : fetchedInvoices.length)
          : Number(counts?.all) || (Number.isFinite(listTotal) ? listTotal : fetchedInvoices.length);
      const derivedTotalPages = Math.max(
        1,
        Math.ceil(Math.max(0, totalForPaging) / CLIENT_INVOICE_PAGE_SIZE),
      );
      const apiTotalPages = Number(pagination?.totalPages);
      const resolvedTotalPages = Math.max(
        1,
        Number.isFinite(apiTotalPages) && apiTotalPages >= 1
          ? Math.max(apiTotalPages, derivedTotalPages)
          : derivedTotalPages,
      );
      setInvoiceListTotalPages(resolvedTotalPages);
      if (invoiceListTab === 0) {
        setListTabCounts({
          all: Number(counts?.all) || (Number.isFinite(listTotal) ? listTotal : fetchedInvoices.length),
          archived:
            Number(counts?.archived) ||
            fetchedInvoices.filter((inv) => isInvoiceArchivedOrDiscarded(inv)).length,
        });
      } else {
        const archivedTotal =
          Number(counts?.archived) ||
          (Number.isFinite(listTotal) ? listTotal : fetchedInvoices.length);
        setListTabCounts((prev) => ({
          all: Number(counts?.all) || prev.all,
          archived: archivedTotal,
        }));
      }
      setSelectedInvoice((prev) => {
        if (!prev?.id) return prev;
        return fetchedInvoices.find((inv) => inv.id === prev.id) ?? prev;
      });

      /** KPI totals are for the whole client — only refresh from the unfiltered "All" list. */
      if (invoiceListTab === 0) {
        if (data?.summary) {
          setTotalBilled(data.summary.totalBilled || 0);
          setTotalPaid(data.summary.totalPaid || 0);
          setTotalDue(data.summary.totalDue ?? data.summary.totalOutstanding ?? 0);
        } else {
          if (data?.totalBilled !== undefined) setTotalBilled(data.totalBilled);
          else {
            const billed = fetchedInvoices.reduce((sum, inv) => sum + (Number(inv.totalDue) || Number(inv.amount) || 0), 0);
            setTotalBilled(billed);
          }

          if (data?.totalPaid !== undefined) setTotalPaid(data.totalPaid);
          else {
            const paid = fetchedInvoices
              .filter(inv => (inv.status || '').toLowerCase() === 'paid')
              .reduce((sum, inv) => sum + (Number(inv.totalDue) || Number(inv.amount) || 0), 0);
            setTotalPaid(paid);
          }

          if (data?.totalDue !== undefined) setTotalDue(data.totalDue);
          else {
            const due = fetchedInvoices
              .filter(inv => {
                const status = (inv.status || '').toLowerCase();
                return status === 'due' || status === 'overdue' || status === 'unpaid';
              })
              .reduce((sum, inv) => sum + (Number(inv.totalDue) || Number(inv.amount) || 0), 0);
            setTotalDue(due);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch invoices', error);
    } finally {
      if (useFullPageSpinner) {
        setIsLoading(false);
        expectFullPageSpinnerRef.current = false;
      } else {
        setIsListLoading(false);
      }
    }
  }, [clientId, invoiceListTab, invoiceListPage, nutritionistCurrency]);

  useEffect(() => {
    if (isAuthLoading) return;
    void loadInvoices();
  }, [isAuthLoading, loadInvoices]);

  const tabLabels = [
    { label: 'All', count: listTabCounts.all },
    { label: 'Archived', count: listTabCounts.archived },
  ] as const;

  const showInvoiceListShell =
    !isLoading && (listTabCounts.all > 0 || listTabCounts.archived > 0 || invoices.length > 0);
  const invoiceDisplayCurrency = nutritionistCurrency;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const handleStatusChange = async (invoice: Invoice, event: SelectChangeEvent<StatusSelectValue>) => {
    const next = event.target.value as StatusSelectValue;
    const current = normalizeStatusForSelect(invoice.status);
    if (next === current) return;

    if (next === 'paid' || next === 'discarded') {
      setInvoiceStatusFeedback(null);
      setPendingStatusChange({ invoice, next });
      return;
    }

    if (!invoice.id) return;
    setStatusUpdatingId(invoice.id);
    try {
      await updateInvoiceStatus(invoice.id, { status: mapUiStatusToApiStatus(next) });
      toast.success('Invoice status updated');
      await loadInvoices();
    } catch (error) {
      console.error('Failed to update invoice status', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleConfirmPendingStatusChange = async () => {
    const change = pendingStatusChange;
    if (!change?.invoice?.id) {
      setInvoiceStatusFeedback({
        variant: 'error',
        title: 'Unable to update invoice status',
        message: 'Select a valid invoice before confirming this status change.',
      });
      return;
    }
    setStatusUpdatingId(change.invoice.id);
    try {
      await updateInvoiceStatus(
        change.invoice.id,
        change.next === 'paid'
          ? {
              status: 'paid',
              paidAt: new Date().toISOString(),
              paymentMethod: LIST_MARK_PAID_PAYMENT_METHOD,
            }
          : { status: mapUiStatusToApiStatus(change.next) }
      );
      toast.success('Invoice status updated');
      setInvoiceStatusFeedback(null);
      setPendingStatusChange(null);
      await loadInvoices();
    } catch (error) {
      console.error('Failed to update invoice status', error);
      setInvoiceStatusFeedback({
        variant: 'error',
        title: 'Could not update invoice status',
        message: getApiErrorMessage(error),
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>, rowIndex: number) => {
    setActionsAnchorEl(event.currentTarget);
    setActiveActionRowIndex(rowIndex);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
    setActiveActionRowIndex(null);
  };

  const isActionsMenuOpen = Boolean(actionsAnchorEl);
  const menuTargetInvoice =
    activeActionRowIndex !== null && activeActionRowIndex >= 0 ? invoices[activeActionRowIndex] : undefined;

  const handleSendReminder = async (invoiceId: string) => {
    if (remindingInvoiceId) return;
    setRemindingInvoiceId(invoiceId);
    try {
      await resendInvoiceEmail(invoiceId);
      toast.success('Reminder email sent');
    } catch (error) {
      console.error('Failed to send invoice reminder', error);
      toast.error('Could not send reminder. Please try again.');
    } finally {
      setRemindingInvoiceId(null);
    }
  };

  const handleInvoiceConfirmDialogConfirm = () => {
    const pending = pendingInvoiceConfirm;
    if (!pending?.invoice?.id) {
      setInvoiceConfirmFeedback({
        variant: 'error',
        title: 'Unable to update invoice',
        message: 'Select a valid invoice before confirming this action.',
      });
      return;
    }
    const { variant, invoice } = pending;

    if (variant === 'delete') {
      void (async () => {
        setDeletingInvoiceId(invoice.id);
        try {
          await deleteClientInvoice(invoice.id);
          toast.success('Invoice deleted');
          if (selectedInvoice?.id === invoice.id) {
            setIsInvoiceDetailsOpen(false);
            setSelectedInvoice(null);
          }
          setInvoiceConfirmFeedback(null);
          setPendingInvoiceConfirm(null);
          await loadInvoices();
        } catch (error) {
          console.error('Failed to delete invoice', error);
          setInvoiceConfirmFeedback({
            variant: 'error',
            title: 'Could not delete invoice',
            message: getApiErrorMessage(error),
          });
        } finally {
          setDeletingInvoiceId(null);
        }
      })();
      return;
    }

    const restoring = variant === 'unarchive';
    void (async () => {
      setStatusUpdatingId(invoice.id);
      try {
        if (restoring) {
          await unarchiveClientInvoice(invoice.id);
        } else {
          await updateInvoiceStatus(invoice.id, { status: 'archived' });
        }
        toast.success(restoring ? 'Invoice unarchived' : 'Invoice archived');
        setInvoiceConfirmFeedback(null);
        setPendingInvoiceConfirm(null);
        await loadInvoices();
      } catch (error) {
        console.error(restoring ? 'Failed to unarchive invoice' : 'Failed to archive invoice', error);
        setInvoiceConfirmFeedback({
          variant: 'error',
          title: restoring ? 'Could not unarchive invoice' : 'Could not archive invoice',
          message: getApiErrorMessage(error),
        });
      } finally {
        setStatusUpdatingId(null);
      }
    })();
  };

  const handleInvoiceActionClick = (action: InvoiceActionKey) => {
    if (activeActionRowIndex === null) {
      handleCloseActionsMenu();
      return;
    }

    const invoice = invoices[activeActionRowIndex];
    handleCloseActionsMenu();

    if (action === 'view') {
      if (!invoice) return;
      setSelectedInvoice(invoice);
      setIsInvoiceDetailsOpen(true);
      return;
    }

    if (action === 'reminder') {
      if (!invoice?.id || !canSendInvoiceReminder(invoice)) return;
      void handleSendReminder(invoice.id);
      return;
    }

    if (action === 'download') {
      if (!invoice?.id) return;
      void (async () => {
        setPdfDownloadingId(invoice.id);
        try {
          const full = await getClientInvoice(invoice.id);
          const fallbackFrom = await getInvoicePdfFallbackFromProfile({
            name: user?.fullName,
            email: user?.email,
          });
          const payload = await withInvoiceClinicLogoDataUrl(
            full.id || invoice.id,
            invoiceApiToPdfPayload(full, fallbackFrom)
          );
          await downloadInvoicePdf(payload);
          toast.success('Invoice PDF downloaded');
        } catch (error) {
          console.error('Invoice PDF export failed', error);
          toast.error(getApiErrorMessage(error));
        } finally {
          setPdfDownloadingId(null);
        }
      })();
      return;
    }

    if (action === 'delete') {
      if (!invoice?.id) return;
      setInvoiceConfirmFeedback(null);
      setPendingInvoiceConfirm({ variant: 'delete', invoice });
      return;
    }

    if (action === 'archive') {
      if (!invoice?.id) return;
      /** Match `InvoicingPage`: Unarchive only from the Archived tab; All tab always archives. */
      const unarchive = invoiceListTab === 1;
      setInvoiceConfirmFeedback(null);
      setPendingInvoiceConfirm({
        variant: unarchive ? 'unarchive' : 'archive',
        invoice,
      });
    }
  };

  if (isAuthLoading || isLoading) {
    return (
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        <Paper elevation={0} sx={{
          border: '1px solid #e2e8f0', borderRadius: '12px', py: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#64748b' }}>Total Billed</Typography>
          <Typography sx={{ mt: 0.5, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '1.5rem', color: '#000000' }}>{formatCurrencyAmount(totalBilled, invoiceDisplayCurrency)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{
          border: '1px solid #e2e8f0', borderRadius: '12px', py: 1.5, bgcolor: '#f0fdf4', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#28c76f' }}>Paid</Typography>
          <Typography sx={{ mt: 0.5, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '1.5rem', color: '#28c76f' }}>{formatCurrencyAmount(totalPaid, invoiceDisplayCurrency)}</Typography>
        </Paper>
        <Paper elevation={0} sx={{
          border: '1px solid #e2e8f0', borderRadius: '12px', py: 1.5, bgcolor: '#fef2f2', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#ea5455' }}>Due</Typography>
          <Typography sx={{ mt: 0.5, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '1.5rem', color: '#ea5455' }}>{formatCurrencyAmount(totalDue, invoiceDisplayCurrency)}</Typography>
        </Paper>
      </Box>

      {showInvoiceListShell ? (
        <>
          <Box sx={invoicingTabsToolbarRowSx}>
              <Tabs
                value={invoiceListTab}
                onChange={(_, newTab) => {
                  setInvoiceListTab(newTab);
                  setInvoiceListPage(1);
                }}
              sx={{
                minHeight: 44,
                '& .MuiTabs-indicator': { backgroundColor: '#20aedc', height: '1.5px' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 44,
                  px: 'clamp(16px, 1.2vw, 28px)',
                  py: 1.5,
                  minWidth: 0,
                  color: '#717182',
                  fontSize: '16px',
                  lineHeight: '20px',
                  fontWeight: 400,
                },
                '& .Mui-selected': { color: '#20aedc', fontWeight: 500 },
              }}
            >
              {tabLabels.map((tab, tabIndex) => (
                <Tab
                  key={tab.label}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>{tab.label}</span>
                      <Tag
                        variant="neutral"
                        size="xs"
                        className={`px-2 py-0.5 ${invoiceListTab === tabIndex ? 'font-bold' : 'font-normal'}`}
                        sx={{
                          ...tabCountChipSx,
                          bgcolor: invoiceListTab === tabIndex ? '#20aedc' : '#ececf0',
                          color: invoiceListTab === tabIndex ? '#fff' : '#717182',
                        }}
                      >
                        {tab.count}
                      </Tag>
                    </Box>
                  }
                />
              ))}
            </Tabs>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pb: 1 }}>
              <Button
                disableElevation
                onClick={() => setIsCreateInvoiceOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={createInvoiceToolbarButtonSx}
              >
                Create Invoice
              </Button>
            </Box>
          </Box>

          <Paper elevation={0} sx={{ mt: 2.1, borderRadius: '16px', border: '1px solid #ebebeb', overflow: 'hidden' }}>
          <Box sx={{ position: 'relative' }}>
            {isListLoading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.75)',
                }}
              >
                <CircularProgress size={28} sx={{ color: '#20AEDC' }} />
              </Box>
            ) : null}
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: { xs: 0, md: 900 } }}>
              <Box
              sx={{
                ...rowGrid,
                display: { xs: 'none', md: 'grid' },
                bgcolor: '#fafafa',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', px: 1 }}>INVOICE #</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', px: 1 }}>SERVICE</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', textAlign: 'center' }}>DATE</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', textAlign: 'center' }}>DUE DATE</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', textAlign: 'center' }}>AMOUNT</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', textAlign: 'center' }}>STATUS</Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#6e6e6e', textAlign: 'center' }}>ACTIONS</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {invoices.map((invoice, index) => {
                const statusValue = normalizeStatusForSelect(invoice.status);
                const statusColor = getStatusColor(statusValue);
                const busy = statusUpdatingId === invoice.id;

                return (
                  <Box
                    key={invoice.id || index}
                    sx={{
                      ...rowGrid,
                      bgcolor: '#fff',
                      borderBottom: index < invoices.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}
                  >
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#646464', overflowWrap: 'anywhere', px: 1 }}>
                      {invoice.invoiceNo || invoice.invoiceNumber || invoice.id || `INV-${index}`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 1 }}>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#646464', overflowWrap: 'anywhere' }}>
                        {invoice.lineItems?.[0]?.description || invoice.service || 'Service'}
                      </Typography>
                      {invoice.lineItems && invoice.lineItems.length > 1 && (
                        <Box sx={{ 
                          bgcolor: '#f1f5f9', 
                          color: '#475569', 
                          px: 1, 
                          py: 0.25, 
                          borderRadius: '12px', 
                          fontSize: '0.75rem', 
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          border: '1px solid #e2e8f0'
                        }}>
                          +{invoice.lineItems.length - 1} more
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#646464', textAlign: { xs: 'left', md: 'center' } }}>
                      {formatDate(invoice.issueDate || invoice.date || invoice.createdAt)}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#646464', textAlign: { xs: 'left', md: 'center' } }}>
                      {formatDate(invoice.dueDate)}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem', color: '#1d1d1d', textAlign: { xs: 'left', md: 'center' } }}>
                      {formatInvoiceAmount(invoice)}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <FormControl size="small">
                        <Select<StatusSelectValue>
                          value={statusValue}
                          onChange={(e) => void handleStatusChange(invoice, e)}
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            ...selectSx,
                            '& .MuiSelect-select': {
                              ...selectSx['& .MuiSelect-select'],
                              color: statusColor,
                            },
                          }}
                          renderValue={(selected) => (
                            <Box component="span" sx={{ color: statusColor, fontWeight: 600, fontSize: '0.875rem' }}>
                              {busy ? '…' : statusLabelForSelect(selected)}
                            </Box>
                          )}
                        >
                          <MenuItem value="due">Due</MenuItem>
                          <MenuItem value="overdue">Overdue</MenuItem>
                          <MenuItem value="paid">Paid</MenuItem>
                          <MenuItem value="discarded">Discarded</MenuItem>
                        </Select>
                      </FormControl>
                      {busy ? (
                        <CircularProgress size={18} sx={{ ml: 1, alignSelf: 'center' }} />
                      ) : null}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenActionsMenu(event, index)}
                        disabled={
                          deletingInvoiceId === invoice.id ||
                          statusUpdatingId === invoice.id ||
                          pdfDownloadingId === invoice.id ||
                          remindingInvoiceId === invoice.id
                        }
                        sx={{ color: '#111', p: 0.6 }}
                        aria-label="Invoice actions"
                        aria-controls={isActionsMenuOpen ? 'client-invoice-actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={isActionsMenuOpen ? 'true' : undefined}
                      >
                        <EllipsisVertical size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {invoices.length === 0 ? (
              <Box sx={{ py: 5, px: 2, textAlign: 'center', bgcolor: '#fff' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.92rem', color: '#344054' }}>
                  {invoiceListTab === 1 ? 'No archived invoices' : 'No invoices found'}
                </Typography>
                {invoiceListTab === 1 ? (
                  <Typography sx={{ mt: 0.6, fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#667085' }}>
                    Archived and discarded invoices for this client will appear here.
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            </Box>
          </Box>
          </Box>
            {invoices.length > 0 ? (
              <Box
                sx={{
                  py: 2.5,
                  display: 'flex',
                  justifyContent: 'center',
                  borderTop: '1px solid #ebebeb',
                  bgcolor: '#fff',
                }}
              >
                <PaginationControls
                  currentPage={invoiceListPage}
                  totalPages={invoiceListTotalPages}
                  onPageChange={setInvoiceListPage}
                  pageSize={CLIENT_INVOICE_PAGE_SIZE}
                  totalCount={invoiceListTab === 1 ? listTabCounts.archived : listTabCounts.all}
                  isLoading={isListLoading}
                  showSummary={false}
                  hideWhenSinglePage={false}
                />
              </Box>
            ) : null}
          </Paper>
        </>
      ) : (
        <>
          <Box sx={{ ...invoicingTabsToolbarRowSx, justifyContent: 'flex-end' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pb: 1 }}>
              <Button
                disableElevation
                onClick={() => setIsCreateInvoiceOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={createInvoiceToolbarButtonSx}
              >
                Create Invoice
              </Button>
            </Box>
          </Box>
          <Paper
            elevation={0}
            sx={{
              mt: 2.1,
              borderRadius: '16px',
              border: '1px solid #ebebeb',
              py: 6,
              px: 2,
              textAlign: 'center',
              bgcolor: '#fff',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.92rem',
                color: '#344054',
              }}
            >
              No invoices found
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8rem',
                color: '#667085',
              }}
            >
              Invoices for this client will appear here.
            </Typography>
          </Paper>
        </>
      )}

      <Menu
        id="client-invoice-actions-menu"
        anchorEl={actionsAnchorEl}
        open={isActionsMenuOpen}
        onClose={handleCloseActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.8,
              minWidth: 260,
              borderRadius: '14px',
              border: '1px solid #eceff3',
              bgcolor: '#fbfcff',
              boxShadow: '0px 16px 30px rgba(20, 24, 33, 0.12)',
              overflow: 'hidden',
              p: 0.5,
            },
          },
        }}
      >
        {invoiceActionItems.map((actionItem) => {
          if (actionItem.key === 'reminder' && !canSendInvoiceReminder(menuTargetInvoice)) return null;

          const showUnarchive = actionItem.key === 'archive' && invoiceListTab === 1;
          const isReminderAction = actionItem.key === 'reminder';
          const reminderBusy = isReminderAction && remindingInvoiceId === menuTargetInvoice?.id;
          const rowLabel = reminderBusy ? 'Sending...' : showUnarchive ? 'Unarchive' : actionItem.label;
          const rowIconAlt = showUnarchive ? 'Unarchive invoice' : actionItem.iconAlt;
          return (
          <MenuItem
            key={actionItem.key}
            onClick={() => handleInvoiceActionClick(actionItem.key)}
            disabled={reminderBusy || (isReminderAction && remindingInvoiceId !== null)}
            sx={{
              borderRadius: '10px',
              py: 1.1,
              px: 1.1,
              gap: 1.05,
              '&:hover': { bgcolor: '#f0f4f8' },
            }}
          >
            {actionItem.key === 'reminder' ? (
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: INVOICE_MENU_REMINDER_ICON_COLOR,
                }}
              >
                {reminderBusy ? (
                  <CircularProgress size={actionItem.iconSize} thickness={5} sx={{ color: INVOICE_MENU_REMINDER_ICON_COLOR }} />
                ) : (
                  <Mail size={actionItem.iconSize} strokeWidth={2} />
                )}
              </Box>
            ) : actionItem.key === 'download' ? (
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: INVOICE_MENU_DOWNLOAD_ICON_COLOR,
                }}
              >
                <Download size={actionItem.iconSize} strokeWidth={2} />
              </Box>
            ) : (
              <Box
                component="img"
                src={actionItem.iconSrc}
                alt={rowIconAlt}
                sx={{ width: actionItem.iconSize, height: actionItem.iconSize, display: 'block', flexShrink: 0 }}
              />
            )}
            <ListItemText
              primary={rowLabel}
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#717989',
                lineHeight: 1.25,
              }}
            />
          </MenuItem>
          );
        })}
      </Menu>

      <CreateInvoiceFlowDialog
        open={isCreateInvoiceOpen}
        onClose={() => {
          setIsCreateInvoiceOpen(false);
        }}
        initialClientId={clientId}
        initialInvoice={null}
        onInvoiceUpdated={() => void loadInvoices()}
      />
      <InvoiceDetailsDialog
        open={isInvoiceDetailsOpen}
        onClose={() => {
          setIsInvoiceDetailsOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        onInvoiceUpdated={() => void loadInvoices()}
      />

      <InvoiceStatusConfirmDialog
        open={Boolean(pendingStatusChange)}
        targetStatus={pendingStatusChange?.next === 'paid' ? 'paid' : pendingStatusChange?.next === 'discarded' ? 'discarded' : null}
        isSubmitting={Boolean(statusUpdatingId)}
        feedback={invoiceStatusFeedback}
        onCancel={() => {
          setInvoiceStatusFeedback(null);
          setPendingStatusChange(null);
        }}
        onConfirm={() => void handleConfirmPendingStatusChange()}
      />
      <InvoiceConfirmDialog
        open={Boolean(pendingInvoiceConfirm)}
        variant={pendingInvoiceConfirm?.variant ?? 'delete'}
        isSubmitting={
          Boolean(pendingInvoiceConfirm) &&
          (deletingInvoiceId === pendingInvoiceConfirm.invoice.id || statusUpdatingId === pendingInvoiceConfirm.invoice.id)
        }
        feedback={invoiceConfirmFeedback}
        onCancel={() => {
          const p = pendingInvoiceConfirm;
          if (!p) return;
          if (deletingInvoiceId === p.invoice.id || statusUpdatingId === p.invoice.id) return;
          setInvoiceConfirmFeedback(null);
          setPendingInvoiceConfirm(null);
        }}
        onConfirm={() => void handleInvoiceConfirmDialogConfirm()}
      />
    </Box>
  );
};
